/**
 * The context object (nav spec §4.7): what the focused tab is, what the dock
 * says it is about, and the text the user last selected on it. Consumed by the
 * dock header, the Go box's Ask, and — later — any voice layer.
 */
import { useMemo } from 'react';

import { useSecondaryPaneVisible } from '@features/workspace/hooks/useSecondaryPaneVisible';
import { useWorkspaceStore } from '@features/workspace/stores/useWorkspaceStore';
import {
  selectActiveDescriptor,
  useChatDockRegistry,
} from '@shared/components/chat/dock/useChatDockRegistry';

import { focusedTab, tabKey } from './focusedTab';
import { useSelectionStore } from './selectionStore';

import type { FocusedTab } from './focusedTab';
import type { SurfaceContext } from '@shared/components/chat/dock/chatDockTypes';

/** What the app is about right now. */
export interface ActiveContext {
  /** The focused group's active tab path. */
  path: string;
  /** The catalog destination that tab shows, or null outside the catalog. */
  destinationId: string | null;
  /**
   * What the dock is currently about — the active surface's chip (kind +
   * entity), or null when none is registered. The registry is "last surface
   * registered", and pane routes in EITHER pane register surfaces, so with a
   * split open this can belong to the other pane. Matching it to the focused
   * pane needs descriptors to carry their pane group — a follow-up.
   */
  surface: SurfaceContext | null;
  /** Text selected on that tab, retained across focus moves; null once the tab changes. */
  selection: string | null;
}

/**
 * The focused tab, from the workspace store. Shared by the context object and
 * the selection recorder so both stamp and match on the same key.
 */
export function useFocusedTab(): FocusedTab {
  const tabs = useWorkspaceStore(s => s.tabs);
  const primaryActiveTabId = useWorkspaceStore(s => s.primaryActiveTabId);
  const secondaryActiveTabId = useWorkspaceStore(s => s.secondaryActiveTabId);
  const storedFocusedGroup = useWorkspaceStore(s => s.focusedGroup);
  const secondaryVisible = useSecondaryPaneVisible();
  return useMemo(
    () => focusedTab(tabs, primaryActiveTabId, secondaryActiveTabId, storedFocusedGroup, secondaryVisible),
    [tabs, primaryActiveTabId, secondaryActiveTabId, storedFocusedGroup, secondaryVisible]
  );
}

/** The context object for the focused tab. */
export function useActiveContext(): ActiveContext {
  const focused = useFocusedTab();
  const path = focused.tab?.path ?? '';
  const key = tabKey(focused);
  const surface = useChatDockRegistry(s => selectActiveDescriptor(s)?.context ?? null);
  const retained = useSelectionStore(s => s.selection);
  const selection = retained && retained.tabKey === key ? retained.text : null;
  const destinationId = focused.tab?.destinationId ?? null;
  return useMemo(
    () => ({ path, destinationId, surface, selection }),
    [path, destinationId, surface, selection]
  );
}
